import type { Point } from '../../shared/contracts.js';
import { overlaps, type CanvasBox } from './canvasGeometry.js';

export const ENTITY_CLEARANCE = 40;
const translationCandidates = (moving: CanvasBox[], obstacles: CanvasBox[], gap: number): Point[] => {
  const xs = new Set([0]), ys = new Set([0]);
  for (const box of moving) for (const other of obstacles) {
    xs.add(other.x - gap - box.x - box.width); xs.add(other.x + other.width + gap - box.x);
    ys.add(other.y - gap - box.y - box.height); ys.add(other.y + other.height + gap - box.y);
  }
  // First inspect one-axis adjustments, preserving the user's other coordinate.
  const x = [...xs].sort((a,b) => Math.abs(a)-Math.abs(b)).slice(0,80);
  const y = [...ys].sort((a,b) => Math.abs(a)-Math.abs(b)).slice(0,80);
  return x.flatMap(dx => y.map(dy => ({x:dx,y:dy}))).sort((a,b) => Math.hypot(a.x,a.y)-Math.hypot(b.x,b.y) || Math.abs(a.y)-Math.abs(b.y));
};
function nearestTranslation(moving: CanvasBox[], obstacles: CanvasBox[], gap: number): Point | null {
  if (!moving.some(box => obstacles.some(other => overlaps(box,other,gap)))) return {x:0,y:0};
  for (const delta of translationCandidates(moving,obstacles,gap)) {
    if (moving.every(box => obstacles.every(other => !overlaps({...box,x:box.x+delta.x,y:box.y+delta.y},other,gap)))) return delta;
  }
  // The diagram is unbounded. This deterministic outer position always exists.
  const right = Math.max(...obstacles.map(box=>box.x+box.width),0)+gap;
  const left = Math.min(...moving.map(box=>box.x));
  return {x:right-left,y:0};
}

/** Repairs only collisions caused by edited appearances. Existing unrelated
 * overlaps are reported to the user elsewhere, never silently rewritten on load.
 * A group translates as one shape. Neighbor pushing is bounded and transactional.
 */
export function resolveEntityPlacement(before: readonly CanvasBox[], proposed: readonly CanvasBox[], editedIds: readonly string[], pushNeighbors = true, gap = ENTITY_CLEARANCE): CanvasBox[] {
  const edited = new Set(editedIds), old = new Map(before.map(box=>[box.id,box]));
  const boxes = proposed.map(box => ({...box}));
  const moving = boxes.filter(box=>edited.has(box.id));
  if (!moving.length) return boxes;
  // A pinned appearance may grow, but its position remains fixed.
  for (const box of moving) if (box.pinned && old.has(box.id)) { box.x=old.get(box.id)!.x; box.y=old.get(box.id)!.y; }
  // Multiple new/imported or simultaneously expanded appearances can collide
  // within the edited set. Repair those before preserving the group externally.
  for(let i=0;i<moving.length;i++) {
    const current=moving[i]!, prior=moving.slice(0,i);
    if(!prior.some(box=>overlaps(current,box,gap)))continue;
    if(current.pinned) {
      for(const other of prior.filter(box=>overlaps(current,box,gap))) {
        if(other.pinned)throw new Error('Pinned entities overlap. Unlock a position before repairing the layout.');
        const shift=nearestTranslation([other],moving.filter(box=>box!==other),gap)!;other.x+=shift.x;other.y+=shift.y;
      }
    } else {const shift=nearestTranslation([current],prior,gap)!;current.x+=shift.x;current.y+=shift.y;}
  }
  const initial = boxes.map(box=>({...box}));
  let failed = false;
  if (pushNeighbors) {
    const queue = [...moving], settled = new Set(edited), limit = Math.min(128,boxes.length*4);
    for (let step=0;queue.length && step<limit;step++) {
      const current = queue.shift()!;
      for (const other of boxes) {
        if (other.id===current.id || settled.has(other.id) || !overlaps(current,other,gap)) continue;
        if (other.pinned) { failed=true; break; }
        const blockers = boxes.filter(box=>box.id!==other.id && (settled.has(box.id)||box.pinned));
        const delta = nearestTranslation([other],blockers,gap)!;
        other.x+=delta.x;other.y+=delta.y;settled.add(other.id);queue.push(other);
      }
      if (failed) break;
    }
    failed ||= queue.length>0 || boxes.some(box=>settled.has(box.id) && boxes.some(other=>other.id!==box.id && !edited.has(other.id) && overlaps(box,other,gap)));
    if (!failed) return boxes;
  }
  // Preserve neighbors if pushing is disabled or blocked by pinned positions.
  for(let i=0;i<boxes.length;i++) Object.assign(boxes[i]!,initial[i]);
  const obstacles=boxes.filter(box=>!edited.has(box.id));
  if (moving.some(box=>box.pinned)) {
    if (moving.some(box=>obstacles.some(other=>overlaps(box,other,gap)))) throw new Error('This size change needs more room around a pinned entity. Unlock its position or move neighboring entities.');
    return boxes;
  }
  const delta=nearestTranslation(moving,obstacles,gap)!;
  for (const box of moving) {box.x+=delta.x;box.y+=delta.y;}
  return boxes;
}

export function findEntityOverlaps(boxes: readonly CanvasBox[]): [string,string][] {
  const result:[string,string][]=[];
  for(let i=0;i<boxes.length;i++)for(let j=i+1;j<boxes.length;j++)if(overlaps(boxes[i]!,boxes[j]!))result.push([boxes[i]!.id,boxes[j]!.id]);
  return result;
}

/** Fit the actual geometry into an asymmetric usable viewport. */
export function fittedViewport(boxes: readonly CanvasBox[], width:number,height:number, inset:{top:number;right:number;bottom:number;left:number}, maxZoom=2) {
  if (!boxes.length || width<=0 || height<=0) return {x:0,y:0,zoom:1};
  const minX=Math.min(...boxes.map(b=>b.x)),minY=Math.min(...boxes.map(b=>b.y));
  const w=Math.max(...boxes.map(b=>b.x+b.width))-minX,h=Math.max(...boxes.map(b=>b.y+b.height))-minY;
  const usableW=Math.max(1,width-inset.left-inset.right),usableH=Math.max(1,height-inset.top-inset.bottom);
  const zoom=Math.max(.02,Math.min(maxZoom,usableW/Math.max(w,1),usableH/Math.max(h,1)));
  return {zoom,x:inset.left+(usableW-w*zoom)/2-minX*zoom,y:inset.top+(usableH-h*zoom)/2-minY*zoom};
}

export type VisibleCanvas = { x:number;y:number;width:number;height:number };
/** Nearest clear placement within the visible diagram-space rectangle. */
export function visibleEntityPosition(boxes:readonly CanvasBox[],size:{width:number;height:number},visible:VisibleCanvas,preferred?:Point,gap=ENTITY_CLEARANCE):Point|null {
  if(size.width>visible.width||size.height>visible.height)return null;
  const clamp=(p:Point)=>({x:Math.max(visible.x,Math.min(visible.x+visible.width-size.width,p.x)),y:Math.max(visible.y,Math.min(visible.y+visible.height-size.height,p.y))});
  const target=clamp(preferred??{x:visible.x+(visible.width-size.width)/2,y:visible.y+(visible.height-size.height)/2});
  const xs=new Set([target.x,visible.x,visible.x+visible.width-size.width]),ys=new Set([target.y,visible.y,visible.y+visible.height-size.height]);
  for(const box of boxes) { xs.add(box.x-size.width-gap);xs.add(box.x+box.width+gap);ys.add(box.y-size.height-gap);ys.add(box.y+box.height+gap); }
  const candidates=[...xs].flatMap(x=>[...ys].map(y=>({x,y}))).filter(p=>p.x>=visible.x&&p.y>=visible.y&&p.x+size.width<=visible.x+visible.width&&p.y+size.height<=visible.y+visible.height).sort((a,b)=>Math.hypot(a.x-target.x,a.y-target.y)-Math.hypot(b.x-target.x,b.y-target.y));
  return candidates.find(p=>boxes.every(box=>!overlaps({id:'new',objectId:'new',...p,...size},box,gap)))??null;
}
